import type React from 'react';
import { AuthButton } from '@/core/permissions/AuthButton';
import type { PermissionPolicy, PermissionPolicyForm } from '../services/permissionPolicy.service';
import type { PermissionRole } from '../types/permissionsList.types';

interface PermissionPolicyEditorBlockProps {
  roles: PermissionRole[];
  policies: PermissionPolicy[];
  permissionOptions: Array<{ key: string; label: string }>;
  form: PermissionPolicyForm;
  isLoading: boolean;
  isSaving: boolean;
  editingId: string | null;
  onFormChange: (form: PermissionPolicyForm) => void;
  onSave: () => void;
  onReset: () => void;
  onEdit: (policy: PermissionPolicy) => void;
  onRemove: (policy: PermissionPolicy) => void;
}

export function PermissionPolicyEditorBlock({
  roles,
  policies,
  permissionOptions,
  form,
  isLoading,
  isSaving,
  editingId,
  onFormChange,
  onSave,
  onReset,
  onEdit,
  onRemove,
}: PermissionPolicyEditorBlockProps) {
  return (
    <section className="min-w-0 overflow-hidden rounded-md border border-slate-100 bg-white p-3 shadow-sm">
      <div className="min-w-0 space-y-2 text-sm">
        <div className="min-w-0">
          <h2 className="truncate font-medium text-slate-900">Scope/Policy editor</h2>
          <p className="line-clamp-2 text-slate-500">Tạo allow/deny policy theo vai trò, quyền, phạm vi và điều kiện nghiệp vụ.</p>
        </div>

        <div className="grid min-w-0 gap-3 xl:grid-cols-[1fr_1fr]">
          <div className="min-w-0 rounded-md border border-slate-100 p-3">
            <div className="grid min-w-0 gap-2 sm:grid-cols-2">
              <Select label="Vai trò" value={form.roleId} onChange={(value) => onFormChange({ ...form, roleId: value })}>
                {roles.map((role) => <option key={role.id} value={role.id}>{role.name}</option>)}
              </Select>
              <Select label="Quyền" value={form.permissionKey} onChange={(value) => onFormChange({ ...form, permissionKey: value })}>
                <option value="">Chọn quyền</option>
                {permissionOptions.map((item) => <option key={item.key} value={item.key}>{item.label}</option>)}
              </Select>
              <Select label="Phạm vi" value={form.scopeType} onChange={(value) => onFormChange({ ...form, scopeType: value as PermissionPolicyForm['scopeType'] })}>
                <option value="company">Công ty</option>
                <option value="warehouse">Kho</option>
                <option value="project">Dự án</option>
                <option value="branch">Chi nhánh</option>
                <option value="own">Dữ liệu của tôi</option>
                <option value="team">Team</option>
                <option value="custom">Tùy chỉnh</option>
              </Select>
              <Select label="Hiệu lực" value={form.effect} onChange={(value) => onFormChange({ ...form, effect: value as PermissionPolicyForm['effect'] })}>
                <option value="allow">Allow</option>
                <option value="deny">Deny</option>
              </Select>
              <label className="min-w-0 space-y-2 text-sm sm:col-span-2">
                <span className="font-semibold text-slate-600">ID phạm vi, cách nhau bằng dấu phẩy</span>
                <input
                  value={form.scopeValueText}
                  onChange={(event) => onFormChange({ ...form, scopeValueText: event.target.value })}
                  className="h-10 w-full min-w-0 rounded-md border border-slate-200 px-3 text-sm outline-none"
                  placeholder="warehouse-id-1, warehouse-id-2"
                />
              </label>
              <label className="min-w-0 space-y-2 text-sm">
                <span className="font-semibold text-slate-600">Giới hạn số tiền</span>
                <input
                  value={form.maxAmount}
                  onChange={(event) => onFormChange({ ...form, maxAmount: event.target.value })}
                  className="h-10 w-full min-w-0 rounded-md border border-slate-200 px-3 text-sm outline-none"
                  placeholder="10000000"
                />
              </label>
              <label className="min-w-0 space-y-2 text-sm">
                <span className="font-semibold text-slate-600">Ưu tiên</span>
                <input
                  type="number"
                  value={form.priority}
                  onChange={(event) => onFormChange({ ...form, priority: Number(event.target.value) })}
                  className="h-10 w-full min-w-0 rounded-md border border-slate-200 px-3 text-sm outline-none"
                />
              </label>
              <Check label="Own only" checked={form.ownOnly} onChange={(checked) => onFormChange({ ...form, ownOnly: checked })} />
              <Check label="Cấm tự duyệt" checked={form.denySelfApproval} onChange={(checked) => onFormChange({ ...form, denySelfApproval: checked })} />
              <Check label="Đang hoạt động" checked={form.isActive} onChange={(checked) => onFormChange({ ...form, isActive: checked })} />
            </div>
            <div className="flex gap-2">
              <AuthButton
                module="permissions"
                action="update"
                behavior="disable"
                permissionReason="Bạn không có quyền cập nhật phân quyền"
                disabled={isSaving}
                onClick={onSave}
                className="h-10 flex-1 rounded-md bg-emerald-600 px-3 text-xs font-medium text-white disabled:opacity-50"
              >
                {editingId ? 'Lưu policy' : 'Tạo policy'}
              </AuthButton>
              <AuthButton
                module="permissions"
                action="update"
                behavior="disable"
                permissionReason="Bạn không có quyền cập nhật phân quyền"
                disabled={isSaving}
                onClick={onReset}
                className="h-10 rounded-md border border-slate-200 bg-white px-3 text-xs font-medium text-slate-700 disabled:opacity-50"
              >
                Làm mới
              </AuthButton>
            </div>
          </div>

          <div className="min-w-0 rounded-md border border-slate-100 p-3">
            <div className="max-h-96 overflow-auto">
              {isLoading ? (
                Array.from({ length: 4 }).map((_, index) => <div key={index} className="mb-2 h-12 animate-pulse rounded-md bg-slate-50" />)
              ) : policies.length ? (
                <div className="space-y-2">
                  {policies.map((policy) => (
                    <div key={policy.id} className="min-w-0 rounded-md border border-slate-100 p-3">
                      <div className="space-y-2 text-sm">
                        <div className="flex min-w-0 items-start justify-between gap-2">
                          <div className="min-w-0">
                            <p className="truncate font-medium text-slate-900">{policy.permissionKey}</p>
                            <p className="truncate text-slate-500">{policy.scopeType} / {policy.effect} / priority {policy.priority}</p>
                          </div>
                          <span className="shrink-0 rounded-md bg-slate-100 px-2 py-1 text-sm font-semibold text-slate-600">
                            {policy.isActive ? 'Active' : 'Off'}
                          </span>
                        </div>
                        <div className="flex gap-2">
                          <AuthButton module="permissions" action="update" behavior="hide" disabled={isSaving} onClick={() => onEdit(policy)} className="h-9 rounded-md border border-slate-200 bg-white px-3 text-xs font-medium text-slate-700">Sửa</AuthButton>
                          <AuthButton module="permissions" action="delete" behavior="hide" disabled={isSaving} onClick={() => onRemove(policy)} className="h-9 rounded-md border border-rose-100 bg-rose-50 px-3 text-xs font-medium text-rose-700">Xóa</AuthButton>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="rounded-md bg-slate-50 p-3 text-sm text-slate-500">Chưa có policy.</p>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Select({ label, value, onChange, children }: { label: string; value: string; onChange: (value: string) => void; children: React.ReactNode }) {
  return (
    <label className="min-w-0 space-y-2 text-sm">
      <span className="font-semibold text-slate-600">{label}</span>
      <select value={value} onChange={(event) => onChange(event.target.value)} className="h-10 w-full min-w-0 rounded-md border border-slate-200 bg-white px-3 text-sm text-slate-700 outline-none">
        {children}
      </select>
    </label>
  );
}

function Check({ label, checked, onChange }: { label: string; checked: boolean; onChange: (checked: boolean) => void }) {
  return (
    <label className="flex min-w-0 items-center gap-2 rounded-md border border-slate-100 p-3 text-sm">
      <input type="checkbox" checked={checked} onChange={(event) => onChange(event.target.checked)} />
      <span className="truncate font-semibold text-slate-700">{label}</span>
    </label>
  );
}
